import type { DataViewConnectionType } from 'cadenza/repository/model/data-view-connection-type.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import {
  type ChartDataViewVisualizationKey,
  asChartDataViewVisualizationKey,
  isReferenceLine,
  isTrendLine,
} from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ChartDataViewSettingsKey } from 'cadenza/workbook/model/chart-data-view-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { DataViewNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import {
  getAnalyticLineDataViewsBySourceDataDataView,
  getReferenceLineSourceDataViewByTargetDataViewId,
} from 'cadenza/workbook/state/workbook-data-view-connections-selectors.state';
import {
  getDataViewById,
  getDataViewSetting,
  getDataViewsByViewId,
  getNotificationsForDataView,
  getViewByDataViewId,
  getViewById,
  getVisualizationKeyByDataViewId,
  isCombinationChart,
  isInCombinationChart,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { getColorClassificationAttribute } from 'cadenza/workbook/workbook-panel/settings/chart/chart-coloring-utils';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

const VISUALIZATIONS_WITH_LEGEND: ChartDataViewVisualizationKey[] = [
  // bar
  'CHART.GROUPED_BAR_CHART',
  'CHART.MULTI_MEASURE_GROUPED_BAR_CHART',
  'CHART.STACKED_BAR_CHART',
  'CHART.MULTI_MEASURE_STACKED_BAR_CHART',
  'CHART.HUNDRED_PERCENT_BAR_CHART',
  'CHART.MULTI_MEASURE_HUNDRED_PERCENT_BAR_CHART',
  // column
  'CHART.GROUPED_COLUMN_CHART',
  'CHART.MULTI_MEASURE_GROUPED_COLUMN_CHART',
  'CHART.STACKED_COLUMN_CHART',
  'CHART.MULTI_MEASURE_STACKED_COLUMN_CHART',
  'CHART.HUNDRED_PERCENT_COLUMN_CHART',
  'CHART.MULTI_MEASURE_HUNDRED_PERCENT_COLUMN_CHART',
  // mirror
  'CHART.MIRROR_BAR_CHART',
  'CHART.MULTI_MEASURE_MIRROR_BAR_CHART',
  // line
  'CHART.MULTI_LINE_CHART',
  'CHART.MULTI_MEASURE_MULTI_LINE_CHART',
  // area
  'CHART.STACKED_AREA_CHART',
  'CHART.MULTI_MEASURE_STACKED_AREA_CHART',
  // pie
  'CHART.PIE_CHART',
  'CHART.MULTI_MEASURE_PIE_CHART',
];

export function isChartLegendEnabled(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const visualizationKey = getVisualizationKeyByDataViewId(state, dataViewId);
  const chartVisualizationKey = asChartDataViewVisualizationKey(visualizationKey);
  const view = getViewByDataViewId(state, dataViewId);
  return (
    isCombinationChart(state, view.id) ||
    getColorClassificationAttribute(dataViewId) != null ||
    VISUALIZATIONS_WITH_LEGEND.includes(chartVisualizationKey) ||
    hasAnalyticLines(state, dataViewId)
  );
}

export const hasReferenceLine = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getDataViewsByViewId(state, viewId).some((dataView) => isReferenceLine(dataView.visualizationKey));
};

export const hasTrendLine = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getDataViewsByViewId(state, viewId).some((dataView) => isTrendLine(dataView.visualizationKey));
};

export const getConnectedAnalyticLinesValidity = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const referenceLineNotifications = [
    ...getReferenceLinesNotifications(state, dataViewId),
    ...getTrendLinesNotifications(state, dataViewId),
  ];
  return getValidity(referenceLineNotifications);
};

export const getAnalyticLineValidity = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const notifications = getNotificationsForDataView(state, getViewByDataViewId(state, dataViewId).id, dataViewId);
  return getValidity(notifications);
};

function getValidity(notifications: DataViewNotification[]) {
  const isValid = !notifications.some((n) => n.level === 'ERROR' || n.level === 'FATAL');
  const hasWarning = isValid && notifications.some((n) => n.level === 'WARNING');
  return {
    isValid,
    hasWarning,
  };
}

export const getNotificationsForDataViewAndConnectedAnalyticLines = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
) => {
  return [
    ...getNotificationsForDataView(state, getViewByDataViewId(state, dataViewId).id, dataViewId),
    ...getReferenceLinesNotifications(state, dataViewId),
    ...getTrendLinesNotifications(state, dataViewId),
  ];
};

const getReferenceLinesNotifications = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getAnalyticLineNotifications(state, dataViewId, 'REFERENCE_LINE');
};

const getTrendLinesNotifications = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getAnalyticLineNotifications(state, dataViewId, 'TREND_LINE');
};

function getAnalyticLineNotifications(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  connectionType: DataViewConnectionType,
) {
  return getDataViewById(state, dataViewId)
    .dataViewToDataViewConnections.filter((c) => c.connectionType === connectionType)
    .flatMap(({ targetDataViewId }) =>
      getNotificationsForDataView(state, getViewByDataViewId(state, dataViewId).id, targetDataViewId),
    );
}

const hasAnalyticLines = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getAnalyticLineDataViewsBySourceDataDataView(state, dataViewId).length > 0;
};

export const getChartDataViewNameWithSuffixIfApplicable = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
) => {
  const dataView = getDataViewById(state, dataViewId);
  // Connected main data-view's name is added as suffix for reference line in combination-chart to highlight for which dataView the reference line belongs to
  const suffix =
    isReferenceLine(dataView.visualizationKey) && isInCombinationChart(state, dataView.id) ?
      ` (${getReferenceLineSourceDataViewByTargetDataViewId(state, dataView.id).name})`
    : '';
  return dataView.name + suffix;
};

export function anyDataViewHasLogarithmicValueAxisEnabled(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  return getViewById(state, viewId).dataViews.some((dataViewId) =>
    getDataViewSetting<boolean>(
      state,
      dataViewId,
      ChartDataViewSettingsKey.toPersistenceKey('VALUE_AXIS_USE_LOGARITHMIC_SCALE'),
    ),
  );
}

export function anyDataViewHasLogarithmicCategoryAxisEnabled(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  return getViewById(state, viewId).dataViews.some((dataViewId) =>
    getDataViewSetting<boolean>(
      state,
      dataViewId,
      ChartDataViewSettingsKey.toPersistenceKey('CATEGORY_AXIS_USE_LOGARITHMIC_SCALE'),
    ),
  );
}
